import { useCurrentTenant } from "@drift/oneplatfront";
import {
    FormHeaderTitle,
    Infobox,
    TelematicData,
    TelematicDigitalSignatureData,
    useWsGet
} from "@onefront/workspace-forms";
import { Box, Button, Stack, useTheme } from "@vapor/react-material";
import { ExtendedTypography } from "@vapor/react-extended";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { IntermediaryListDto } from "../../../../../../../@types/dtos/intermediaries/IntermediaryListDto";
import { IntermediaryFooterForm } from "./IntermediaryFooterForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "@onefront/react-sdk";

export interface IntermediaryTelematicDataFormProps {
    onSubmit: (data: any) => void;
    onBack: () => void;
    loadingGet?: boolean;
    loadingPost?: boolean;
    mode?: "create" | "edit";
    data?: IntermediaryListDto;
}

export const IntermediaryTelematicDataForm = ({ onSubmit, onBack, loadingGet, loadingPost, mode, data, taxId }) => {
    const {
        palette: {
            primary: { textSubduedColor: Cadet }
        }
    } = useTheme();

    const { tenant } = useCurrentTenant(true);
    const intermediaryTaxId = useParams()?.intermediaryTaxId;
    const [displayErrors, setDisplayErrors] = useState<any>(false);
    const [isEditing, setIsEditing] = useState<any>(mode === "CREATE");
    const [errors, setErrors] = useState<any>(null);
    const [telematicData, setTelematicData] = useState<any>({
        enabledNotifications: [],
        printSignature: true
    });
    const [telematicSignData, setTelematicSignData] = useState<any>();

    const { t } = useTranslation();

    const {
        data: intermediary,
        fetch: fetchIntermediary,
        loading
    } = useWsGet(
        `rnd:core:intermediary-registry-read:api://v1/${tenant?.itemId}/intermediary/${intermediaryTaxId || taxId}`,
        {
            lazy: false,
            params: { withSignatureImageFile: true }
        }
    );

    useEffect(() => {
        if (intermediaryTaxId || taxId) {
            fetchIntermediary({
                url: `rnd:core:intermediary-registry-read:api://v1/${tenant?.itemId}/intermediary/${
                    intermediaryTaxId || taxId
                }`,
                params: { withSignatureImageFile: true }
            });
        }
        //eslint-disable-next-line
    }, [intermediaryTaxId, taxId, tenant?.itemId]);

    useEffect(() => {
        if (!loading && intermediary) {
            const {
                type,
                telematicData: {
                    digitalSigner,
                    enabledNotifications,
                    isDigitalSignatureOnSenderEnabled,
                    id,
                    signatureImageFile,
                    ...rest
                } = {
                    digitalSigner: null,
                    enabledNotifications: [],
                    isDigitalSignatureOnSenderEnabled: false,
                    id: null,
                    signatureImageFile: null
                }
            } = intermediary;

            if (intermediary) {
                setTelematicData({
                    classifier: type,
                    enabledNotifications,
                    printSignature: true
                });
            }

            if (intermediary?.telematicData) {
                if (signatureImageFile) {
                    signatureImageFile.size = atob(signatureImageFile?.content)?.length;
                }
                setTelematicData({
                    classifier: type,
                    signatureImageFile,
                    enabledNotifications,
                    ...rest
                });
                setTelematicSignData({
                    digitalSigner,
                    isDigitalSignatureOnSenderEnabled
                });
            }
        }
    }, [intermediary, loading]);

    const handleSubmit = () => {
        setDisplayErrors(errors);

        if (Object.keys(errors).some(key => errors[key])) {
            return;
        } else {
            if (mode === "EDIT") {
                setIsEditing(false);
            }
            onSubmit({
                ...telematicData,
                ...telematicSignData
            });
        }
    };

    return (
        <Stack spacing={5} direction="column" maxWidth={780}>
            <Box component={"div"} gap={3} flexDirection={"column"} display={"flex"}>
                <FormHeaderTitle
                    title={"Dati telematici"}
                    subtitle="Inserisci informazioni sui dati telematici"
                    onEdit={() => {
                        setIsEditing(true);
                    }}
                    sx={{ mb: 0 }}
                />
                <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                    <ExtendedTypography variant="titleSmall" color={Cadet}>
                        Invio telematico
                    </ExtendedTypography>
                    {mode === "EDIT" && !isEditing && !loadingPost && (
                        <Button
                            onClick={() => {
                                setIsEditing(true);
                                setErrors({ ...errors, vatNumber: false, taxId: false });
                            }}
                            variant="text"
                        >
                            <FontAwesomeIcon icon={faEdit} style={{ marginRight: 8 }} /> {t("general.edit")}
                        </Button>
                    )}
                </Stack>
                <TelematicData
                    key="intermediary-data"
                    body={telematicData}
                    editing={mode === "CREATE" || (mode === "EDIT" && isEditing)}
                    loading={
                        loading ||
                        (mode === "EDIT" && loadingGet) ||
                        (mode === "EDIT" && loadingPost) ||
                        (intermediaryTaxId && loadingGet)
                    }
                    onChange={(data: any, key: any, value: any) => {
                        setDisplayErrors((prev: any) => {
                            return {
                                ...prev,
                                [key]: false
                            };
                        });

                        setTelematicData(data);
                    }}
                    onError={(err: any) => {
                        setErrors((prev: any) => {
                            return {
                                ...prev,
                                ...err
                            };
                        });
                    }}
                    displayErrors={displayErrors}
                    required
                />
            </Box>

            <Box>
                <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} mb={3}>
                    <ExtendedTypography variant="titleSmall" color={Cadet}>
                        Firma digitale
                    </ExtendedTypography>
                </Stack>
                {isEditing && telematicData?.classifier === "C" && (
                    <Infobox
                        title="Informazioni sulla firma digitale"
                        description="Quando l'intermediario è una persona giuridica e viene usata la firma digitale è necassario avere il codice fiscale di una persona fisica sulla coccarda di firma che imprime la firma digitale."
                        variant="standard"
                    />
                )}
                <TelematicDigitalSignatureData
                    body={telematicSignData}
                    type={telematicData?.classifier}
                    onChange={(data: any, key: any, value: any) => {
                        setDisplayErrors((prev: any) => {
                            return {
                                ...prev,
                                [key]: false
                            };
                        });

                        setTelematicSignData(data);
                    }}
                    loading={
                        loading ||
                        (mode === "EDIT" && loadingGet) ||
                        (mode === "EDIT" && loadingPost) ||
                        (intermediaryTaxId && loadingGet)
                    }
                    editing={mode === "CREATE" || (mode === "EDIT" && isEditing)}
                    onError={(err: any) => {
                        setErrors((prev: any) => {
                            return {
                                ...prev,
                                ...err
                            };
                        });
                    }}
                    displayErrors={displayErrors}
                    required
                />
            </Box>

            {isEditing && (
                <IntermediaryFooterForm
                    onBack={() => {
                        if (mode === "CREATE") {
                            onBack?.();
                        } else {
                            const {
                                type,
                                telematicData: {
                                    digitalSigner,
                                    enabledNotifications,
                                    isDigitalSignatureOnSenderEnabled,
                                    id,
                                    signatureImageFile,
                                    ...rest
                                } = {
                                    digitalSigner: null,
                                    enabledNotifications: [],
                                    isDigitalSignatureOnSenderEnabled: false,
                                    id: null,
                                    signatureImageFile: null
                                }
                            } = intermediary;

                            if (intermediary) {
                                setTelematicData({
                                    classifier: type,
                                    enabledNotifications,
                                    printSignature: true
                                });
                            }

                            if (intermediary?.telematicData) {
                                if (signatureImageFile) {
                                    signatureImageFile.size = atob(signatureImageFile?.content)?.length;
                                }
                                setTelematicData({
                                    classifier: type,
                                    signatureImageFile,
                                    enabledNotifications,
                                    ...rest
                                });
                                setTelematicSignData({
                                    digitalSigner,
                                    isDigitalSignatureOnSenderEnabled
                                });
                            }
                            setIsEditing(false);
                        }
                    }}
                    onForward={handleSubmit}
                    disabledForward={loadingPost}
                    loading={loadingPost}
                    isNew={mode === "CREATE"}
                    lastStep={false}
                />
            )}
        </Stack>
    );
};
